import { createContext, FC, PropsWithChildren, useCallback, useContext, useState } from 'react';

interface WindowedPanelInfo {
  id: string;
  label: string;
}

interface PanelManagerContextValue {
  detachPanel: (panelId: string) => void;
  dockPanel: (panelId: string) => void;
  isPanelWindowed: (panelId: string) => boolean;
  windowedPanels: WindowedPanelInfo[];
  registerPanel: (panelId: string, label: string) => void;
  unregisterPanel: (panelId: string) => void;
  focusPanel: (panelId: string) => void;
}

const PanelManagerContext = createContext<PanelManagerContextValue | null>(null);

export const panelWindowId = (panelId: string) => `panel-${panelId}`;

export const MAIN_WINDOW_NAME = 'ankar-main';

export const usePanelManager = (): PanelManagerContextValue => {
  'use memo';
  const ctx = useContext(PanelManagerContext);
  if (!ctx) {
    throw new Error('usePanelManager must be used within a PanelManager');
  }
  return ctx;
};

export const PanelManager: FC<PropsWithChildren> = ({ children }) => {
  'use memo';

  const [windowedPanelIds, setWindowedPanelIds] = useState<Set<string>>(new Set());
  const [labelRegistryRef, setLabelRegistryRef] = useState<Map<string, string>>(new Map());

  const detachPanel = useCallback((panelId: string) => {
    setWindowedPanelIds(prev => new Set([...prev, panelId]));
  }, []);

  const dockPanel = useCallback((panelId: string) => {
    setWindowedPanelIds(prev => {
      const next = new Set(prev);
      next.delete(panelId);
      return next;
    });
  }, []);

  const isPanelWindowed = useCallback((panelId: string) => windowedPanelIds.has(panelId), [windowedPanelIds]);

  // Brings an already-detached popup to the foreground. Relies on the named-window
  // pattern in PanelPortal: re-calling window.open with the same name and an empty
  // URL returns the existing window without renavigating it (so its loaded shell
  // and rendered content are preserved), and we can call .focus() on the handle.
  const focusPanel = useCallback(
    (panelId: string) => {
      if (!windowedPanelIds.has(panelId)) return;
      const popup = window.open('', panelWindowId(panelId));
      popup?.focus();
    },
    [windowedPanelIds],
  );

  const registerPanel = useCallback((panelId: string, label: string) => {
    setLabelRegistryRef(prev => {
      const next = new Map(prev);
      next.set(panelId, label);
      return next;
    });
  }, []);

  const unregisterPanel = useCallback((panelId: string) => {
    setLabelRegistryRef(prev => {
      const next = new Map(prev);
      next.delete(panelId);
      return next;
    });
    // If the panel was windowed when it was removed (e.g. closed via the X
    // button while in a popup), evict it from the windowed set
    setWindowedPanelIds(prev => {
      if (!prev.has(panelId)) return prev;
      const next = new Set(prev);
      next.delete(panelId);
      return next;
    });
  }, []);

  const windowedPanels = Array.from(windowedPanelIds).map(id => ({
    id,
    label: labelRegistryRef.get(id) ?? id,
  }));

  return (
    <PanelManagerContext.Provider
      value={{
        detachPanel,
        dockPanel,
        isPanelWindowed,
        windowedPanels,
        registerPanel,
        unregisterPanel,
        focusPanel,
      }}
    >
      {children}
    </PanelManagerContext.Provider>
  );
};
